'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface BookingFormProps {
  className?: string;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  date: string;
  time: string;
  adults: number;
  children: number;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  date?: string;
  time?: string;
}

const BookingForm = ({ className = '' }: BookingFormProps) => {
  const [form, setForm] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    adults: 2,
    children: 0,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [rateLimitRetryAfter, setRateLimitRetryAfter] = useState<number | null>(null);

  const validateField = (name: string, value: string | number): string | undefined => {
    switch (name) {
      case 'name':
        if (!String(value).trim()) return 'Name is required';
        if (String(value).trim().length < 2) return 'Name must be at least 2 characters';
        return undefined;
      case 'email': {
        if (!String(value).trim()) return 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(String(value))) return 'Please enter a valid email address';
        return undefined;
      }
      case 'phone': {
        if (!String(value).trim()) return 'Phone number is required';
        const phoneRegex = /^\+?[\d\s\-()]+$/;
        if (!phoneRegex.test(String(value)) || String(value).replace(/\D/g, '').length < 10)
          return 'Please enter a valid phone number';
        return undefined;
      }
      case 'date':
        if (!String(value).trim()) return 'Date is required';
        const selectedDate = new Date(String(value));
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (selectedDate < today) return 'Please select a future date';
        return undefined;
      case 'time':
        if (!String(value).trim()) return 'Time is required';
        return undefined;
      default:
        return undefined;
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    const numValue = name === 'adults' || name === 'children' ? Number(value) : value;
    setForm((prev) => ({ ...prev, [name]: numValue }));

    if (touched[name]) {
      const error = validateField(name, String(value));
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setRateLimitRetryAfter(null);

    const newErrors: FormErrors = {};
    newErrors.name = validateField('name', form.name);
    newErrors.email = validateField('email', form.email);
    newErrors.phone = validateField('phone', form.phone);
    newErrors.date = validateField('date', form.date);
    newErrors.time = validateField('time', form.time);

    const hasErrors = Object.values(newErrors).some((error) => error !== undefined);

    if (hasErrors) {
      setErrors(newErrors);
      setTouched({ name: true, email: true, phone: true, date: true, time: true });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/table-booking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: form.name,
          email: form.email,
          phone: form.phone,
          date: form.date,
          time: form.time,
          numberOfAdults: form.adults,
          numberOfChildren: form.children,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.status === 429) {
        setSubmitError(data.error || 'Too many attempts. Please wait a moment before trying again.');
        setRateLimitRetryAfter(data.retryAfterSeconds ?? 10);
        return;
      }

      if (!response.ok) {
        setSubmitError(data.error || 'Unable to complete your reservation. Please try again.');
        return;
      }

      setSubmitSuccess(true);
      setForm({
        name: '',
        email: '',
        phone: '',
        date: '',
        time: '',
        adults: 2,
        children: 0,
      });
      setErrors({});
      setTouched({});
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 3000);
    } catch {
      setSubmitError('Something went wrong. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`rounded-lg bg-card shadow-md ${className}`}>
      <div className="border-b border-border px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading text-xl font-semibold text-foreground">Table Booking Form</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Fill out the form below to reserve your table at Freshlymade.
            </p>
          </div>
          <Icon name="CalendarDaysIcon" size={28} className="text-primary" />
        </div>
      </div>

      <form onSubmit={onSubmit} className="space-y-6 px-6 py-6">
        {/* Name Field */}
        <div>
          <label htmlFor="name" className="text-caption mb-2 block text-foreground">
            Full Name <span className="text-error">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={form.name}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`w-full rounded-lg border px-4 py-3 text-sm transition-smooth focus-ring ${
              errors.name && touched.name
                ? 'border-error bg-error/5'
                : 'border-border bg-background'
            }`}
            placeholder="Durga Shankar"
            disabled={isSubmitting}
          />
          {errors.name && touched.name && (
            <p className="mt-1 flex items-center space-x-1 text-xs text-error">
              <Icon name="ExclamationCircleIcon" size={14} />
              <span>{errors.name}</span>
            </p>
          )}
        </div>

        {/* Email Field */}
        <div>
          <label htmlFor="email" className="text-caption mb-2 block text-foreground">
            Email Address <span className="text-error">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`w-full rounded-lg border px-4 py-3 text-sm transition-smooth focus-ring ${
              errors.email && touched.email
                ? 'border-error bg-error/5'
                : 'border-border bg-background'
            }`}
            placeholder="durga@example.com"
            disabled={isSubmitting}
          />
          {errors.email && touched.email && (
            <p className="mt-1 flex items-center space-x-1 text-xs text-error">
              <Icon name="ExclamationCircleIcon" size={14} />
              <span>{errors.email}</span>
            </p>
          )}
        </div>

        {/* Phone Field */}
        <div>
          <label htmlFor="phone" className="text-caption mb-2 block text-foreground">
            Phone Number <span className="text-error">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`w-full rounded-lg border px-4 py-3 text-sm transition-smooth focus-ring ${
              errors.phone && touched.phone
                ? 'border-error bg-error/5'
                : 'border-border bg-background'
            }`}
            placeholder="+91 75420 72555"
            disabled={isSubmitting}
          />
          {errors.phone && touched.phone && (
            <p className="mt-1 flex items-center space-x-1 text-xs text-error">
              <Icon name="ExclamationCircleIcon" size={14} />
              <span>{errors.phone}</span>
            </p>
          )}
        </div>

        {/* Date and Time Fields */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Date Field */}
          <div>
            <label htmlFor="date" className="text-caption mb-2 block text-foreground">
              Date <span className="text-error">*</span>
            </label>
            <div className="relative">
              <Icon
                name="CalendarDaysIcon"
                size={18}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
              />
              <input
                type="date"
                id="date"
                name="date"
                value={form.date}
                onChange={handleChange}
                onBlur={handleBlur}
                min={new Date().toISOString().split('T')[0]}
                className={`w-full rounded-lg border px-4 py-3 pr-10 text-sm transition-smooth focus-ring ${
                  errors.date && touched.date
                    ? 'border-error bg-error/5'
                    : 'border-border bg-background'
                }`}
                disabled={isSubmitting}
              />
            </div>
            {errors.date && touched.date && (
              <p className="mt-1 flex items-center space-x-1 text-xs text-error">
                <Icon name="ExclamationCircleIcon" size={14} />
                <span>{errors.date}</span>
              </p>
            )}
          </div>

          {/* Time Field */}
          <div>
            <label htmlFor="time" className="text-caption mb-2 block text-foreground">
              Time <span className="text-error">*</span>
            </label>
            <div className="relative">
              <Icon
                name="ClockIcon"
                size={18}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
              />
              <input
                type="time"
                id="time"
                name="time"
                value={form.time}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`w-full rounded-lg border px-4 py-3 pr-10 text-sm transition-smooth focus-ring ${
                  errors.time && touched.time
                    ? 'border-error bg-error/5'
                    : 'border-border bg-background'
                }`}
                disabled={isSubmitting}
              />
            </div>
            {errors.time && touched.time && (
              <p className="mt-1 flex items-center space-x-1 text-xs text-error">
                <Icon name="ExclamationCircleIcon" size={14} />
                <span>{errors.time}</span>
              </p>
            )}
          </div>
        </div>

        {/* Number of Guests */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Adults */}
          <div>
            <label htmlFor="adults" className="text-caption mb-2 block text-foreground">
              Number of Adults
            </label>
            <input
              type="number"
              id="adults"
              name="adults"
              min={1}
              max={20}
              value={form.adults}
              onChange={handleChange}
              className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm transition-smooth focus-ring"
              disabled={isSubmitting}
            />
          </div>

          {/* Children */}
          <div>
            <label htmlFor="children" className="text-caption mb-2 block text-foreground">
              Number of Children
            </label>
            <input
              type="number"
              id="children"
              name="children"
              min={0}
              max={20}
              value={form.children}
              onChange={handleChange}
              className="w-full rounded-lg border border-border bg-background px-4 py-3 text-sm transition-smooth focus-ring"
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* Success Message */}
        {submitSuccess && (
          <div className="flex items-center space-x-2 rounded-lg bg-success/10 px-4 py-3 text-success">
            <Icon name="CheckCircleIcon" size={20} />
            <span className="text-sm font-medium">
              Thank you! Your table reservation has been confirmed.
            </span>
          </div>
        )}

        {/* Error / Rate limit Message */}
        {submitError && (
          <div className="flex items-center space-x-2 rounded-lg bg-error/10 px-4 py-3 text-error">
            <Icon name="ExclamationCircleIcon" size={20} />
            <span className="text-sm font-medium">
              {submitError}
              {rateLimitRetryAfter != null && (
                <span className="block mt-1 text-xs">You can try again in {rateLimitRetryAfter} seconds.</span>
              )}
            </span>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex w-full items-center justify-center space-x-2 rounded-lg bg-primary px-6 py-3 font-semibold text-primary-foreground transition-smooth hover:bg-primary/90 focus-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? (
            <>
              <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
              <span>Reserving...</span>
            </>
          ) : (
            <>
              <Icon name="CheckCircleIcon" size={20} />
              <span>Reserve a Table</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default BookingForm;
